import {
	AddRounded,
	CancelRounded,
	CheckCircleRounded,
	DeleteRounded,
	EditRounded,
	SaveRounded,
	ViewListRounded,
	GridViewRounded,
} from "@mui/icons-material";
import { Box, Button, ButtonGroup, Checkbox, FormControl, IconButton, MenuItem, Select, Stack, Typography } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { PimDashboardState } from "context/PimDashboardContext";
import { useEffect, useMemo, useState } from "react";
import {
	normalizeTrimBreakdownSizeData,
	pimTrimBreakdownAssociationObj,
	trimsTableColumns,
	sectionCompletedStatus,
	sectionDetailsColumns,
	trimsDetailsCompleteApi,
	getTrimsCompleteValidationError,
	reconcileTrimBreakdownRows,
	getTrimItemBreakdownRows,
	buildBreakdownPayloadRows,
	isTruthyBreakdownLevel,
} from "../data";
import {
	trimCategoryColumn,
	trimCategoryDropdowns,
	trimCategoryDropdownsObj,
	trimSubCategoryDropdowns,
} from "components/PimDashboard/DashboardComponents/Sourcing/TrimsMaster/data";
import { inputTypes, imageColumn } from "data/globalData";
import { attrGroupLinkedTables, styleColorAssocObj } from "components/PimDashboard/data";
import TrimBreakdownModal from "./TrimBreakdownModal/TrimBreakdownModal";
import { isCatalogDrivenTrimCategory } from "../trimCatalogHelpers";
import SectionCompleteButton from "../SectionCompleteButton";

const colStyle = {
	headerAlign: "center",
	align: "center",
};

export const WhiteBgBox = ({ field, row }) => {
	return (
		<Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center" sx={{ background: "white" }}>
			<span>{row?.[field]}</span>
		</Box>
	);
};

export const RightWrongBox = ({ field, row }) => {
	return (
		<Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
			{row[field] === true ? <CheckCircleRounded color="success" /> : <CancelRounded color="error" />}
		</Box>
	);
};

const TrimsSection = ({
	styleId,
	styleFabricArr = [],
	styleTrimsArr = [],
	sectionDetails,
	onSectionDetailsUpdate = () => {},
	updatedTrimsArr = [],
	setUpdatedTrimsArr = () => {},
	setStyleTrimsArr = () => {},
	publishDisabled,
}) => {
	const { selectedStyle, allAttributes } = PimDashboardState();
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const [viewMode, setViewMode] = useState("table");
	const [isEditMode, setIsEditMode] = useState(false);
	const [trimsArr, setTrimsArr] = useState(styleTrimsArr?.length ? styleTrimsArr : []);
	const [trimToAdd, setTrimToAdd] = useState(() =>
		Object.keys(trimsTableColumns).reduce((acc, row) => {
			acc[trimsTableColumns[row].key] = trimsTableColumns[row]?.defaultValue;
			return acc;
		}, {}),
	);
	const [selectedTrimCategoryDesc, setSelectedTrimCategoryDesc] = useState();
	const [breakdownOpen, setBreakdownOpen] = useState(false);
	const [selectedTrimForBreakdown, setSelectedTrimForBreakdown] = useState();
	const sizeAttrCol = useMemo(() => allAttributes?.find((row) => row?.key === "sizes"), [allAttributes]);
	const selectedStyleColors = useMemo(
		() => selectedStyle?.[attrGroupLinkedTables.color.key] ?? selectedStyle?.[styleColorAssocObj] ?? [],
		[selectedStyle],
	);
	const selectedStyleSizes = useMemo(() => {
		const sizeData =
			(sizeAttrCol?.linked_table && selectedStyle?.[sizeAttrCol.linked_table]?.[sizeAttrCol.key]) ??
			selectedStyle?.[sizeAttrCol?.linked_table]?.sizes ??
			[];

		return normalizeTrimBreakdownSizeData(sizeData);
	}, [selectedStyle, sizeAttrCol]);

	useEffect(() => {
		setTrimsArr(styleTrimsArr?.length ? styleTrimsArr : []);
	}, [styleTrimsArr]);

	const trimCategoryOptions = trimCategoryDropdowns;
	const trimCategoryKey = trimsTableColumns.item_category.key;
	const trimSubCategoryKey = trimsTableColumns.item_sub_category.key;
	const trimColorLevelKey = trimsTableColumns.color_level.key;
	const trimSizeLevelKey = trimsTableColumns.size_level.key;
	const trimChannelLevelKey = trimsTableColumns.channel_level.key;

	const handleCancelClick = () => {
		setTrimsArr(styleTrimsArr?.length ? styleTrimsArr : []);
		setUpdatedTrimsArr([]);
		setTrimToAdd(
			Object.keys(trimsTableColumns).reduce((acc, row) => {
				acc[trimsTableColumns[row].key] = trimsTableColumns[row]?.defaultValue;
				return acc;
			}, {}),
		);
		setSelectedTrimCategoryDesc();
		setSelectedTrimForBreakdown();
		setBreakdownOpen(false);
		setIsEditMode(false);
	};

	const buildTrimPayloadRow = (row) => {
		if (row?.is_deleted) return { id: row.id, is_deleted: true };
		const isNew = row?.is_new === true;
		const breakdownRows = getTrimItemBreakdownRows(row);
		const shouldSaveBreakdown =
			breakdownRows.length > 0 ||
			isCatalogDrivenTrimCategory(row?.item_category) ||
			isTruthyBreakdownLevel(row?.[trimColorLevelKey]) ||
			isTruthyBreakdownLevel(row?.[trimSizeLevelKey]) ||
			isTruthyBreakdownLevel(row?.[trimChannelLevelKey]);
		const payloadBreakdownRows = shouldSaveBreakdown
			? buildBreakdownPayloadRows(breakdownRows, {
					isNewParent: isNew,
					breakdownOnly: row?.is_breakdown_only === true,
				})
			: undefined;

		if (row?.is_breakdown_only) {
			return { id: row.id, is_new: false, [pimTrimBreakdownAssociationObj]: payloadBreakdownRows ?? [] };
		}

		const { id, is_new, is_breakdown_only, [pimTrimBreakdownAssociationObj]: _, ...rest } = row;
		const trimRow = { is_new: isNew, ...rest };
		if (!isNew) trimRow.id = id;
		else delete trimRow.id;
		if (payloadBreakdownRows?.length) trimRow[pimTrimBreakdownAssociationObj] = payloadBreakdownRows;
		return trimRow;
	};

	const handleSaveClick = async () => {
		if (!updatedTrimsArr?.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No Changes To Save",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const payload = { trimsArr: updatedTrimsArr.map(buildTrimPayloadRow) };
			const res = await axios.post(`/api/fabric_trims/save_trim_data?styleId=${styleId}`, payload);
			const created = (res?.data?.result?.createdTrimsData ?? []).map((row) => ({
				...row,
				[pimTrimBreakdownAssociationObj]: row?.[pimTrimBreakdownAssociationObj] ?? row?.trimsBreakDown ?? [],
			}));
			const updated = (res?.data?.result?.updateTrimsData ?? []).map((row) => ({
				...row,
				[pimTrimBreakdownAssociationObj]: row?.[pimTrimBreakdownAssociationObj] ?? row?.trimsBreakDown ?? [],
			}));
			const savedIds = [...created, ...updated].map((r) => r?.id).filter(Boolean);
			const savedTrimsArr = [
				...trimsArr.filter((row) => !row?.is_new && !savedIds.includes(row?.id)).map(({ is_breakdown_only, ...row }) => row),
				...updated,
				...created,
			];
			setStyleTrimsArr(savedTrimsArr);
			setUpdatedTrimsArr([]);
			setIsEditMode(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Trims saved successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || error?.message || "Unable to save trims",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const isSectionCompleted = sectionDetails?.[sectionDetailsColumns.is_completed.key] === sectionCompletedStatus;

	const handleCompleteClick = async () => {
		const validationError = getTrimsCompleteValidationError(trimsArr);
		if (validationError) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: validationError,
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const res = await axios.post(`${trimsDetailsCompleteApi}?styleId=${styleId}`);
			const updatedDetails = res?.data?.result;
			onSectionDetailsUpdate(updatedDetails ?? {});
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Accessories section marked complete",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || error?.message || "Unable to complete section",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleAddTrimGroup = () => {
		const currentTrim = trimToAdd;
		if (
			!currentTrim ||
			currentTrim[trimCategoryKey] === inputTypes.SELECT.key ||
			currentTrim[trimSubCategoryKey] === inputTypes.SELECT.key
		) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Select Details",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		if (currentTrim?.[trimColorLevelKey] === true && !selectedStyleColors.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No colors found in selected style",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		if (currentTrim?.[trimSizeLevelKey] === true && !selectedStyleSizes.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No sizes found in selected style",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const objToAdd = {
			...currentTrim,
			is_new: true,
			id: new Date().valueOf(),
		};
		objToAdd[pimTrimBreakdownAssociationObj] = reconcileTrimBreakdownRows({
			trimObj: objToAdd,
			colorRows: selectedStyleColors,
			sizeRows: selectedStyleSizes,
			existingRows: [],
		});
		setTrimsArr((prev) => [...prev, objToAdd]);
		setUpdatedTrimsArr((prev) => [...prev, objToAdd]);
		setTrimToAdd(
			Object.keys(trimsTableColumns).reduce((acc, row) => {
				acc[trimsTableColumns[row].key] = trimsTableColumns[row]?.defaultValue;
				return acc;
			}, {}),
		);
	};

	const handleCatgorySelectChange = (e) => {
		const categoryValue = e.target.value;
		const arr = trimSubCategoryDropdowns.filter((row) => row?.[trimCategoryColumn.key] === categoryValue);
		setSelectedTrimCategoryDesc(arr);
		setTrimToAdd((prev) => ({
			...prev,
			[trimCategoryKey]: categoryValue,
			[trimSubCategoryKey]: inputTypes.SELECT.key,
		}));
	};

	const openBreakdown = (selectedTrimRow) => {
		const reconciledBreakdownRows = reconcileTrimBreakdownRows({
			trimObj: selectedTrimRow,
			colorRows: selectedStyleColors,
			sizeRows: selectedStyleSizes,
			existingRows: getTrimItemBreakdownRows(selectedTrimRow),
		});
		const trimWithBreakdown = {
			...selectedTrimRow,
			[pimTrimBreakdownAssociationObj]: reconciledBreakdownRows,
		};
		const existingCount = getTrimItemBreakdownRows(selectedTrimRow).length;
		if (reconciledBreakdownRows.length !== existingCount) {
			setTrimsArr((prev) => prev.map((row) => (row?.id === trimWithBreakdown?.id ? trimWithBreakdown : row)));
			setUpdatedTrimsArr((prev) => {
				const existing = prev.find((row) => row?.id === trimWithBreakdown?.id);
				if (existing) {
					return [
						...prev.filter((row) => row?.id !== trimWithBreakdown?.id),
						{ ...trimWithBreakdown, is_breakdown_only: existing?.is_breakdown_only },
					];
				}
				if (trimWithBreakdown?.is_new) {
					return [...prev, trimWithBreakdown];
				}
				return [...prev, { ...trimWithBreakdown, is_breakdown_only: true }];
			});
		}
		setSelectedTrimForBreakdown(trimWithBreakdown);
		setBreakdownOpen(true);
	};

	const handleBreakdownClose = () => {
		setBreakdownOpen(false);
	};

	const handleTrimTableRowUpdate = (updated) => {
		setTrimsArr((prev) => prev.map((row) => (row?.id === updated?.id ? updated : row)));
		setUpdatedTrimsArr((prev) => prev.map((row) => (row?.id === updated?.id ? { ...updated, is_breakdown_only: false } : row)));
		return updated;
	};

	const handleBreakdownTrimUpdate = (updatedTrim) => {
		setTrimsArr((prev) => prev.map((row) => (row?.id === updatedTrim?.id ? updatedTrim : row)));
		setUpdatedTrimsArr((prev) => {
			const existing = prev.find((row) => row?.id === updatedTrim?.id);
			const isBreakdownOnly = !updatedTrim?.is_new && !existing?.is_new && existing?.is_breakdown_only !== false;
			return [...prev.filter((row) => row?.id !== updatedTrim?.id), { ...updatedTrim, is_breakdown_only: isBreakdownOnly }];
		});
		setSelectedTrimForBreakdown(updatedTrim);
		return updatedTrim;
	};

	const handleDeleteTrimsClick = (row) => {
		if (row?.is_new) {
			// never persisted — just remove from UI, no need to tell backend
			setTrimsArr((prev) => prev.filter((r) => r?.id !== row.id));
			setUpdatedTrimsArr((prev) => prev.filter((r) => r?.id !== row.id));
		} else {
			// persisted — remove from UI and mark for deletion in save payload
			setTrimsArr((prev) => prev.filter((r) => r?.id !== row.id));
			setUpdatedTrimsArr((prev) => [...prev.filter((r) => r?.id !== row.id), { ...row, is_deleted: true }]);
		}
	};

	const deftrimsColumns = [
		{
			...trimsTableColumns.item_category,
			flex: 1.2,
			...colStyle,
			headerName: trimsTableColumns.item_category.name,
			field: trimsTableColumns.item_category.key,
			renderCell: ({ field, row }) => (
				<Box component="div" onClick={() => openBreakdown(row)}>
					<span style={{ color: blue[600] }}>{row[field]}</span>
				</Box>
			),
		},
		{
			...trimsTableColumns.item_sub_category,
			field: trimsTableColumns.item_sub_category.key,
			headerName: trimsTableColumns.item_sub_category.name,
			flex: 1.2,
			...colStyle,
		},
		{
			...trimsTableColumns.color_level,
			flex: 0.8,
			...colStyle,
			headerName: trimsTableColumns.color_level.name,
			field: trimsTableColumns.color_level.key,
			renderCell: ({ field, row }) => <RightWrongBox field={field} row={row} />,
		},
		{
			...trimsTableColumns.size_level,
			headerName: trimsTableColumns.size_level.name,
			field: trimsTableColumns.size_level.key,
			flex: 0.8,
			...colStyle,
			renderCell: ({ field, row }) => <RightWrongBox field={field} row={row} />,
		},
		{
			...trimsTableColumns.channel_level,
			headerName: trimsTableColumns.channel_level.name,
			field: trimsTableColumns.channel_level.key,
			flex: 0.8,
			...colStyle,
			renderCell: ({ field, row }) => <RightWrongBox field={field} row={row} />,
		},
		{
			headerName: "Delete",
			flex: 0.8,
			...colStyle,
			renderCell: ({ row }) => (
				<IconButton onClick={() => handleDeleteTrimsClick(row)} disabled={publishDisabled || !isEditMode}>
					<DeleteRounded />
				</IconButton>
			),
		},
	];

	return (
		<Box
			sx={{
				border: `1px solid ${grey[300]}`,
				borderRadius: 2,
				overflow: "hidden",
				backgroundColor: "#fff",
				width: "100%",
				minWidth: 0,
				boxSizing: "border-box",
			}}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="flex-start"
				rowGap={1.5}
				flexWrap="wrap"
				px={2}
				py={1.5}
				sx={{
					backgroundColor: grey[50],
					borderBottom: `1px solid ${grey[300]}`,
				}}>
				<Stack spacing={1.25} flex={1} minWidth={320}>
					<Box display="flex" alignItems="center" columnGap={1.25} flexWrap="wrap" rowGap={0.75}>
						<Typography variant="h6" sx={{ fontWeight: 700, color: grey[900] }}>
							Accessories
						</Typography>
						{isSectionCompleted && (
							<Typography variant="caption" sx={{ color: grey[600] }}>
								Completed by {sectionDetails?.[sectionDetailsColumns.completed_by.key] || "—"}
							</Typography>
						)}
						{!isEditMode && (
							<ButtonGroup size="small" disableElevation sx={{ height: 28 }}>
								<Button
									variant={viewMode === "table" ? "contained" : "outlined"}
									onClick={() => setViewMode("table")}
									startIcon={<ViewListRounded />}
									sx={{ textTransform: "none" }}>
									Table
								</Button>
								<Button
									variant={viewMode === "grid" ? "contained" : "outlined"}
									onClick={() => setViewMode("grid")}
									startIcon={<GridViewRounded />}
									sx={{ textTransform: "none" }}>
									Grid
								</Button>
							</ButtonGroup>
						)}
					</Box>
					{isEditMode && (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								alignItems: "center",
								columnGap: 1,
								rowGap: 1,
							}}>
							<Select
								sx={{
									width: 170,
									backgroundColor: "#fff",
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: grey[300],
									},
									borderRadius: 1,
								}}
								size="small"
								fullWidth
								variant="outlined"
								displayEmpty
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
								value={trimToAdd?.[trimCategoryKey] ?? inputTypes.SELECT.key}
								renderValue={(selected) =>
									selected === inputTypes.SELECT.key ? (
										<span style={{ color: grey[400] }}>Select Category</span>
									) : (
										selected
									)
								}
								onChange={handleCatgorySelectChange}>
								<MenuItem value={inputTypes.SELECT.key}>
									<span style={{ color: grey[400] }}>Select Category</span>
								</MenuItem>
								{trimCategoryOptions?.map((cat) => (
									<MenuItem key={cat.key} value={cat.key}>
										{cat.name}
									</MenuItem>
								))}
							</Select>

							<Select
								sx={{
									width: 170,
									backgroundColor: "#fff",
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: grey[300],
									},
									borderRadius: 1,
								}}
								size="small"
								fullWidth
								variant="outlined"
								displayEmpty
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
								value={trimToAdd?.[trimSubCategoryKey] ?? inputTypes.SELECT.key}
								onChange={(e) => {
									const selectedTrimGrpDesc = selectedTrimCategoryDesc?.find((row) => row.key === e.target.value);
									if (selectedTrimGrpDesc) {
										setTrimToAdd((prev) => ({
											...prev,
											[trimSubCategoryKey]: selectedTrimGrpDesc.key,
										}));
									}
								}}>
								<MenuItem value={inputTypes.SELECT.key}>
									<span style={{ color: grey[400] }}>Select Description</span>
								</MenuItem>
								{selectedTrimCategoryDesc?.map((row) => (
									<MenuItem key={row.key} value={row.key}>
										{row.name}
									</MenuItem>
								))}
							</Select>

							<Box display="flex" alignItems="center">
								<Checkbox
									checked={trimToAdd?.[trimColorLevelKey] === true}
									onChange={(e) =>
										setTrimToAdd((prev) => ({
											...prev,
											[trimColorLevelKey]: e.target.checked,
										}))
									}
									inputProps={{ "aria-label": "controlled" }}
								/>
								<Typography variant="subtitle1">{trimsTableColumns.color_level.name}</Typography>
							</Box>
							<Box display="flex" alignItems="center">
								<Checkbox
									checked={trimToAdd?.[trimSizeLevelKey] === true}
									onChange={(e) =>
										setTrimToAdd((prev) => ({
											...prev,
											[trimSizeLevelKey]: e.target.checked,
										}))
									}
									inputProps={{ "aria-label": "controlled" }}
								/>
								<Typography variant="subtitle1">{trimsTableColumns.size_level.name}</Typography>
							</Box>
							<Box display="flex" alignItems="center">
								<Checkbox
									checked={trimToAdd?.[trimChannelLevelKey] === true}
									onChange={(e) =>
										setTrimToAdd((prev) => ({
											...prev,
											[trimChannelLevelKey]: e.target.checked,
										}))
									}
									inputProps={{ "aria-label": "controlled" }}
								/>
								<Typography variant="subtitle1">{trimsTableColumns.channel_level.name}</Typography>
							</Box>
							<Button size="small" variant="contained" onClick={handleAddTrimGroup} startIcon={<AddRounded />}>
								Add Accessory
							</Button>
						</Box>
					)}
				</Stack>
				<Box display="flex" columnGap={1} alignItems="center" flexWrap="wrap" rowGap={1}>
					{isEditMode ? (
						<>
							<Button
								size="small"
								variant="outlined"
								onClick={handleCancelClick}
								startIcon={<CancelRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Cancel
							</Button>
							<Button
								size="small"
								variant="contained"
								onClick={handleSaveClick}
								startIcon={<SaveRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Save
							</Button>
						</>
					) : (
						<>
							<SectionCompleteButton sectionDetails={sectionDetails} onComplete={handleCompleteClick} />
							<Button
								size="small"
								variant="contained"
								onClick={() => {
									setIsEditMode(true);
									setViewMode("table");
								}}
								startIcon={<EditRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Edit
							</Button>
						</>
					)}
				</Box>
			</Box>
			{viewMode === "table" ? (
				<Box width="100%" sx={{ overflow: "hidden", boxSizing: "border-box" }}>
					<DataGrid
						columns={deftrimsColumns}
						rows={trimsArr}
						getRowId={(row) => row?.id}
						sx={{
							width: "100%",
							maxWidth: "100%",
							height: 170,
							background: "#fff",
							border: "none",
							overflowX: "hidden",
							"& .MuiDataGrid-main": {
								overflowX: "hidden",
							},
							"& .MuiDataGrid-virtualScroller": {
								overflowX: "hidden",
							},
							"& .MuiDataGrid-row": {
								cursor: "pointer",
								backgroundColor: "#fff",
								"&:nth-of-type(even)": {
									backgroundColor: grey[50],
								},
							},
							"& .MuiDataGrid-columnHeader": {
								cursor: "pointer",
								backgroundColor: grey[100],
								borderBottom: `1px solid ${grey[300]}`,
							},
							"& .MuiDataGrid-cell": {
								padding: 0,
								borderBottom: `1px solid ${grey[200]}`,
							},
							"& .MuiDataGrid-cell:focus-within": {
								outline: `1px solid ${blue[400]}`,
								outlineOffset: "-1px",
							},
							"& .MuiDataGrid-columnHeaders": {
								background: grey[100],
								borderBottom: `1px solid ${grey[300]}`,
								borderRadius: 0,
							},
							"& .MuiDataGrid-columnHeaderTitleContainer": {
								overflow: "hidden",
							},
							"& .MuiDataGrid-cellContent": {
								overflow: "hidden",
								textOverflow: "ellipsis",
							},
						}}
						columnHeaderHeight={35}
						rowHeight={35}
						showCellVerticalBorder
						initialState={{
							pagination: {
								paginationModel: { pageSize: 10 },
							},
						}}
						hideFooter
						disableRowSelectionOnClick
						processRowUpdate={handleTrimTableRowUpdate}
					/>
				</Box>
			) : (
				<Box width="100%" sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, boxSizing: "border-box" }}>
					{trimsArr.map((row) => {
						const uniqueBreakdowns = (row[pimTrimBreakdownAssociationObj] || []).reduce((acc, curr) => {
							const rowKey = curr.trim_code || curr.item_description;
							if (rowKey && !acc.some((x) => (x.trim_code || x.item_description) === rowKey)) {
								acc.push(curr);
							}
							return acc;
						}, []);

						return (
							<Box
								key={row.id}
								onClick={() => openBreakdown(row)}
								sx={{
									width: 250,
									border: `1px solid ${grey[300]}`,
									borderRadius: 2,
									overflow: "hidden",
									display: "flex",
									flexDirection: "column",
									cursor: "pointer",
									transition: "all 0.2s",
									"&:hover": {
										boxShadow: `0px 4px 10px rgba(0,0,0,0.1)`,
									},
								}}>
								<Box sx={{ backgroundColor: grey[100], p: 1, borderBottom: `1px solid ${grey[300]}` }}>
									<Typography
										variant="subtitle2"
										fontWeight="700"
										noWrap
										title={`${row[trimCategoryKey] || ""} - ${row[trimSubCategoryKey] || ""}`}>
										{row[trimCategoryKey] || "Category"} - {row[trimSubCategoryKey] || "Description"}
									</Typography>
								</Box>
								<Box sx={{ p: 1, display: "flex", flexWrap: "wrap", gap: 1, maxHeight: 200, overflowY: "auto" }}>
									{uniqueBreakdowns.map((breakdown, idx) => {
										const displayLabel = breakdown.trim_code || breakdown.item_description || "Configured";
										const imageUrl = breakdown[imageColumn.key];

										return (
											<Box
												key={idx}
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													width: 70,
													border: `1px solid ${grey[200]}`,
													borderRadius: 1,
													p: 0.5,
												}}>
												{imageUrl ? (
													<Box
														component="img"
														src={imageUrl}
														alt={displayLabel}
														sx={{ width: "100%", height: 60, objectFit: "contain", borderRadius: 1 }}
													/>
												) : (
													<Box
														sx={{
															width: "100%",
															height: 60,
															backgroundColor: grey[200],
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															borderRadius: 1,
														}}>
														<Typography variant="caption" color="textSecondary" align="center">
															No Image
														</Typography>
													</Box>
												)}
												<Typography
													variant="caption"
													noWrap
													title={displayLabel}
													sx={{ mt: 0.5, width: "100%", textAlign: "center" }}>
													{displayLabel}
												</Typography>
											</Box>
										);
									})}
									{uniqueBreakdowns.length === 0 && (
										<Typography
											variant="body2"
											color="textSecondary"
											sx={{ width: "100%", textAlign: "center", py: 2 }}>
											No Trims Added
										</Typography>
									)}
								</Box>
							</Box>
						);
					})}
					{trimsArr.length === 0 && (
						<Typography variant="body1" color="textSecondary" sx={{ width: "100%", textAlign: "center", py: 4 }}>
							No accessories found.
						</Typography>
					)}
				</Box>
			)}

			<TrimBreakdownModal
				open={breakdownOpen}
				onClose={handleBreakdownClose}
				selectedTrim={trimsArr.find((row) => row?.id === selectedTrimForBreakdown?.id) ?? selectedTrimForBreakdown}
				onUpdateTrim={handleBreakdownTrimUpdate}
				isEditMode={isEditMode}
				styleColorRows={selectedStyleColors}
				styleSizeRows={selectedStyleSizes}
			/>
		</Box>
	);
};

export default TrimsSection;
